import { FC, ReactElement, useCallback, useState } from "react";
import { Box, Button, Stack, Typography, useMediaQuery } from "@mui/material";
import { Theme as MuiTheme } from "@mui/material/styles";

import { LogoutConfirmModal } from "@/components";
import { LogoutIcon } from "@/components/sidebar-icons";
import { useAppDispatch } from "@/store";
import { logout } from "@/services";
import { responsiveFontSizes } from "@/utils/responsive";
import { useNavigate } from "react-router-dom";
import { tokens } from "@/tokens";

interface ILogoutItem {
  navbar?: boolean;
  isSidebarExtended?: boolean;
}

export const LogoutItem: FC<ILogoutItem> = ({
  navbar,
  isSidebarExtended,
}): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const mdUp = useMediaQuery((theme: MuiTheme) => theme.breakpoints.up("md"), {
    defaultMatches: true,
    noSsr: false,
  });

  const extendedSidebar = useCallback(() => {
    if (mdUp) {
      return isSidebarExtended;
    }
    return false;
  }, [mdUp, isSidebarExtended]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleLogoutModalOpen = () => {
    setOpenModal(true);
  };

  const handleLogoutModalClose = () => {
    setOpenModal(false);
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        px: navbar ? 0 : extendedSidebar() ? 2 : 0,
        pb: navbar ? 0 : 1,
      }}
    >
      {navbar ? (
        <Button
          fullWidth
          onClick={handleLogoutModalOpen}
          sx={{
            justifyContent: "flex-start",
            color: tokens.text.muted,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.xs,
            textTransform: "none",
            borderRadius: tokens.radius.md,
            py: 0.5,
            px: 1,
            minHeight: 0,
            '&:hover': { bgcolor: tokens.bg.hover, color: tokens.status.danger },
            '&:hover svg': { color: `${tokens.status.danger} !important` },
          }}
        >
          <Box sx={{ width: 14, height: 14, mr: 1, display: "flex", alignItems: "center" }}>
            <LogoutIcon color={tokens.text.muted} />
          </Box>
          Logout
        </Button>
      ) : !mdUp ? (
        <Button
          variant="outlined"
          onClick={handleLogoutModalOpen}
          sx={{
            borderColor: tokens.border.default,
            borderRadius: tokens.radius.md,
            justifyContent: "flex-start",
            color: tokens.text.secondary,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            textTransform: "none",
          }}
        >
          <Box sx={{ width: 16, height: 16, mr: 1.25, display: "flex", alignItems: "center" }}>
            <LogoutIcon color={tokens.text.muted} />
          </Box>
          Logout
        </Button>
      ) : (
        <Stack
          direction={extendedSidebar() ? "row" : "column"}
          alignItems={"center"}
          justifyContent={extendedSidebar() ? "flex-start" : "center"}
          sx={{
            borderRadius: tokens.radius.md,
            width: extendedSidebar() ? "93%" : "64px",
            height: extendedSidebar() ? "37px" : "64px",
            mx: extendedSidebar() ? "7px" : "auto",
            cursor: "pointer",
            pl: extendedSidebar() ? 2.5 : 0.5,
            my: extendedSidebar() ? 0.2 : 0,
            transition: "background-color 0.2s ease, color 0.2s ease",
            "&:hover": {
              background: tokens.bg.hover,
            },
          }}
          spacing={extendedSidebar() ? 1 : 0.5}
          onClick={handleLogoutModalOpen}
        >
          <Box sx={{ width: extendedSidebar() ? 18 : 16, height: 'auto' }}>
            <LogoutIcon color={tokens.text.muted} />
          </Box>
          <Typography
            color={tokens.text.muted}
            sx={{
              fontWeight: tokens.fontWeight.semibold,
              lineHeight: "normal",
              fontSize: extendedSidebar() ? tokens.fontSize.xl : responsiveFontSizes.xs,
              letterSpacing: "0px",
            }}
          >
            Logout
          </Typography>
        </Stack>
      )}

      {/* Logout modal */}
      <LogoutConfirmModal
        open={openModal}
        onClose={handleLogoutModalClose}
        maxWidth="xs"
        onConfirm={handleLogout}
      />
    </Box>
  );
};
